import { useEffect, useState } from 'react';
import { MAX_PINNED_COLUMNS } from '@common/types/constants';
import { lowerCase, capitalize } from 'lodash';
import { Box, Chip, Tooltip, Typography } from '@mui/material';
import {
  GridColDef,
  GridColumnOrderChangeParams,
  GridColumnResizeParams,
  GridColumnVisibilityModel,
  GridPinnedColumnFields,
  GridSortDirection,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { IPatientsTableFilters } from '@views/pages/Patients/components/PatientsTable/hooks';
import {
  usePatientsListQuery,
  usePatientTablePreferencesQuery,
  useUpdatePatientTablePreferencesMutation,
} from '@common/cache';
import {
  ColumnDensity,
  components,
  paths,
  PatientPreferences,
  SORT_ORDER,
} from '@common/types';
import { PersistedPatientsColumn, theme, usePatientsStore } from '@store';
import {
  buildGridColumns,
  getCellTextWidth,
  getReorderedColumns,
} from '@util/dataGrid';
import { getAccountStatusColor } from '@util/user';
import { MinutesChip, TruncatedTextWithConditionalTooltip } from '@components';
import { GridClickableCell } from '@ui';

export type IRecentPatientTableEntry =
  components['schemas']['FilteredListPatient'];

type BuildRecentPatientColumnsArgs = {
  density?: ColumnDensity;
  handleOpenCMActivitiesModal: (patientId: string) => void;
  displayedColumns: string[];
};

export const buildRecentPatientColumns = ({
  density,
  handleOpenCMActivitiesModal,
  displayedColumns,
}: BuildRecentPatientColumnsArgs): GridColDef<IRecentPatientTableEntry>[] => {
  const columns: GridColDef<IRecentPatientTableEntry>[] = [
    {
      field: 'id',
      headerName: 'ID',
      width: 150,
      minWidth: 150,
      sortable: true,
      filterable: false,
      renderCell: params => (
        <GridClickableCell to={`/patient/${params.row.id}`}>
          <TruncatedTextWithConditionalTooltip
            variant='body2'
            text={params.row.display_id}
            width={getCellTextWidth(params.colDef.computedWidth)}
          />
        </GridClickableCell>
      ),
    },
    {
      field: 'name',
      headerName: 'Name',
      minWidth: 180,
      flex: 1,
      sortable: false,
      filterable: false,
      renderCell: params => (
        <GridClickableCell to={`/patient/${params.row.id}`}>
          <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
            <TruncatedTextWithConditionalTooltip
              variant='body2'
              text={params.row.name}
              width={getCellTextWidth(params.colDef.computedWidth)}
            />
            {density !== 'dense' && (
              <TruncatedTextWithConditionalTooltip
                variant='caption'
                color='textSecondary'
                text={capitalize(params.row.gender)}
                width={getCellTextWidth(params.colDef.computedWidth)}
              />
            )}
          </Box>
        </GridClickableCell>
      ),
    },
    {
      field: 'birthdate',
      headerName: 'Date of birth',
      width: 130,
      minWidth: 130,
      sortable: false,
      filterable: false,
      renderCell: params => (
        <GridClickableCell to={`/patient/${params.row.id}`}>
          <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 120 }}>
            <TruncatedTextWithConditionalTooltip
              text={
                params.row.birthdate
                  ? new Date(params.row.birthdate).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })
                  : ''
              }
              sx={{ fontSize: '14px' }}
              width={getCellTextWidth(params.colDef.computedWidth)}
            />
            {density !== 'dense' && (
              <TruncatedTextWithConditionalTooltip
                text={params.row.age}
                variant='body2'
                color='textSecondary'
                sx={{ fontSize: '12px' }}
                width={getCellTextWidth(params.colDef.computedWidth)}
              />
            )}
          </Box>
        </GridClickableCell>
      ),
    },
    {
      field: 'minutes_this_month',
      headerName: 'CM Minutes',
      width: 140,
      minWidth: 140,
      sortable: false,
      filterable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: params => (
        <Box
          sx={{
            display: 'grid',
            placeItems: 'center',
            width: '100%',
            height: '100%',
          }}
        >
          <Box
            onClick={e => {
              e.stopPropagation();
              e.preventDefault();
              handleOpenCMActivitiesModal(params.row.id);
            }}
          >
            <MinutesChip minutes={params.row.minutes_this_month} clickable />
          </Box>
        </Box>
      ),
    },
    {
      field: 'mbi',
      headerName: 'Primary insurance',
      minWidth: 160,
      flex: 1,
      sortable: false,
      filterable: false,
      renderCell: params => {
        const isMedicare = params.row.insurance_type === 'medicare';
        const hasInsurance = !!params.row.insurance_type;

        const primaryText = isMedicare
          ? params.row.mbi
          : hasInsurance
            ? 'Other'
            : '-';

        const secondaryText =
          hasInsurance && params.row.medicare_payer_name
            ? params.row.medicare_payer_name
            : undefined;

        return (
          <GridClickableCell to={`/patient/${params.row.id}`}>
            <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
              <TruncatedTextWithConditionalTooltip
                width={getCellTextWidth(params.colDef.computedWidth)}
                text={primaryText}
                sx={{ fontSize: '14px' }}
              />
              {density !== 'dense' && secondaryText && (
                <TruncatedTextWithConditionalTooltip
                  text={secondaryText}
                  variant='body2'
                  color='textSecondary'
                  sx={{ fontSize: '12px' }}
                  width={getCellTextWidth(params.colDef.computedWidth)}
                />
              )}
            </Box>
          </GridClickableCell>
        );
      },
    },
    {
      field: 'account_status',
      headerName: 'Account status',
      width: 140,
      minWidth: 140,
      sortable: false,
      filterable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: params => (
        <GridClickableCell to={`/patient/${params.row.id}`}>
          <Box
            sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}
          >
            <Chip
              sx={{
                backgroundColor: getAccountStatusColor(
                  params.row.account_status,
                ),
                color:
                  params.row.account_status === 'Inactive'
                    ? 'rgba(0, 0, 0, 0.6)'
                    : theme.palette.common.white,
                height: '24px',
              }}
              label={params.row.account_status || 'Status unknown'}
            />
          </Box>
        </GridClickableCell>
      ),
    },
    {
      field: 'guide_status',
      headerName: 'GUIDE status',
      minWidth: 160,
      flex: 1,
      sortable: false,
      filterable: false,
      renderCell: params => (
        <GridClickableCell to={`/patient/${params.row.id}`}>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <TruncatedTextWithConditionalTooltip
              text={params.row.guide_status}
              sx={{ fontSize: '14px' }}
              width={getCellTextWidth(params.colDef.computedWidth)}
            />
            {density !== 'dense' && (
              <TruncatedTextWithConditionalTooltip
                text={params.row.guide_tier}
                variant='body2'
                color='textSecondary'
                sx={{ fontSize: '12px' }}
                width={getCellTextWidth(params.colDef.computedWidth)}
              />
            )}
          </Box>
        </GridClickableCell>
      ),
    },
    {
      field: 'programs',
      headerName: 'Programs',
      minWidth: 160,
      flex: 1,
      sortable: false,
      filterable: false,
      renderCell: params => (
        <GridClickableCell to={`/patient/${params.row.id}`}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              minWidth: 120,
            }}
          >
            <TruncatedTextWithConditionalTooltip
              text={
                params.row.programs.length > 0 ? params.row.programs[0] : 'None'
              }
              {...(!params.row.programs.length && {
                color: 'textSecondary',
              })}
              sx={{ fontSize: '14px' }}
              width={getCellTextWidth(params.colDef.computedWidth)}
            />

            {params.row.programs.length > 1 && density !== 'dense' && (
              <Tooltip
                title={
                  <Box sx={{ whiteSpace: 'pre-line' }}>
                    {params.row.programs.slice(1).join('\n')}
                  </Box>
                }
                arrow
              >
                <Box sx={{ display: 'flex' }}>
                  <Typography
                    variant='body2'
                    color='textSecondary'
                    sx={{ fontSize: '12px' }}
                  >
                    {` + ${params.row.programs.length - 1} more`}
                  </Typography>
                </Box>
              </Tooltip>
            )}
          </Box>
        </GridClickableCell>
      ),
    },
    {
      field: 'open_task_count',
      headerName: 'Open tasks',
      width: 110,
      minWidth: 110,
      sortable: false,
      filterable: false,
      renderCell: params => (
        <GridClickableCell to={`/patient/${params.row.id}`}>
          <TruncatedTextWithConditionalTooltip
            text={params.row.open_task_count}
            sx={{ fontSize: '14px' }}
            width={getCellTextWidth(params.colDef.computedWidth)}
          />
        </GridClickableCell>
      ),
    },
    {
      field: 'provider_name',
      headerName: 'PCP',
      width: 160,
      minWidth: 160,
      sortable: false,
      filterable: false,
      renderCell: params => (
        <GridClickableCell to={`/patient/${params.row.id}`}>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <TruncatedTextWithConditionalTooltip
              text={params.row.provider_name}
              sx={{ fontSize: '14px' }}
              width={getCellTextWidth(params.colDef.computedWidth)}
            />
            {density !== 'dense' && (
              <TruncatedTextWithConditionalTooltip
                text={params.row.provider_practice_name}
                variant='body2'
                color='textSecondary'
                sx={{ fontSize: '12px' }}
                width={getCellTextWidth(params.colDef.computedWidth)}
              />
            )}
          </Box>
        </GridClickableCell>
      ),
    },
    {
      field: 'practice_name',
      headerName: 'Practice',
      minWidth: 160,
      flex: 1,
      sortable: false,
      filterable: false,
      renderCell: params => (
        <GridClickableCell to={`/patient/${params.row.id}`}>
          <TruncatedTextWithConditionalTooltip
            text={params.row.practice_name}
            sx={{ fontSize: '14px' }}
            width={getCellTextWidth(params.colDef.computedWidth)}
          />
        </GridClickableCell>
      ),
    },
    {
      field: 'care_navigator_name',
      headerName: 'Care navigator',
      width: 170,
      minWidth: 170,
      sortable: false,
      filterable: false,
      renderCell: params => (
        <GridClickableCell to={`/patient/${params.row.id}`}>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <TruncatedTextWithConditionalTooltip
              text={params.row.care_navigator_name}
              sx={{ fontSize: '14px' }}
              width={getCellTextWidth(params.colDef.computedWidth)}
            />
            {density !== 'dense' && (
              <TruncatedTextWithConditionalTooltip
                text={params.row.care_navigator_team_name}
                variant='body2'
                color='textSecondary'
                sx={{ fontSize: '12px' }}
                width={getCellTextWidth(params.colDef.computedWidth)}
              />
            )}
          </Box>
        </GridClickableCell>
      ),
    },
    {
      field: 'building_name',
      headerName: 'Location',
      width: 180,
      minWidth: 180,
      sortable: false,
      filterable: false,
      renderCell: params => (
        <GridClickableCell to={`/patient/${params.row.id}`}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
            }}
          >
            {params.row.building_id &&
            params.row.building_name &&
            params.row.operator_id &&
            params.row.operator_name ? (
              <>
                <TruncatedTextWithConditionalTooltip
                  text={params.row.building_name}
                  sx={{ fontSize: '14px' }}
                  width={getCellTextWidth(params.colDef.computedWidth)}
                />
                {density !== 'dense' && (
                  <TruncatedTextWithConditionalTooltip
                    variant='body2'
                    color='textSecondary'
                    sx={{ fontSize: '12px' }}
                    text={params.row.operator_name}
                    width={getCellTextWidth(params.colDef.computedWidth)}
                  />
                )}
              </>
            ) : (
              <Typography variant='body1' sx={{ fontSize: '14px' }}>
                Home
              </Typography>
            )}
          </Box>
        </GridClickableCell>
      ),
    },
  ];

  return columns.filter(column => displayedColumns.includes(column.field));
};

const emptyFilters: IPatientsTableFilters = {
  search: '',
  sort_order: 'asc',
  sort_by: 'name',
  page: 1,
  limit: 100,
  phone_number: '',
};

const MIN_VISIBLE_COLUMNS = 4;

export const useRecentPatientTable = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isResetViewModalOpen, setIsResetViewModalOpen] = useState(false);
  const [isCMActivitiesModalOpen, setIsCMActivitiesModalOpen] = useState(false);
  const [selectedPatientId, setSelectedPatientId] = useState('');
  const apiRef = useGridApiRef();
  const searchParamsObj = Object.fromEntries(searchParams.entries());

  const { data: preferences } = usePatientTablePreferencesQuery();

  const initialFilters = {
    ...emptyFilters,
    sort_by: preferences?.sort_by as IPatientsTableFilters['sort_by'],
    sort_order: lowerCase(preferences?.sort_order) as SORT_ORDER,
    limit: preferences?.page_size ?? emptyFilters.limit,
    ...searchParamsObj,
  };

  const [filters, setFilters] = useState<IPatientsTableFilters>({
    ...initialFilters,
  });

  const isFilterDisabled =
    !filters.phone_number && !filters.search && !filters.birthdate;

  const {
    data = { patients: [], count: 0 },
    isFetching,
    isRefetching,
  } = usePatientsListQuery({
    ...filters,
    recently_viewed: isFilterDisabled,
    additionalFilters: '',
  });

  const handleOpenResetViewModal = () => {
    setIsResetViewModalOpen(true);
  };

  const handleCloseResetViewModal = () => {
    setIsResetViewModalOpen(false);
  };

  const handleOpenCMActivitiesModal = (patient_id: string) => {
    setIsCMActivitiesModalOpen(true);
    setSelectedPatientId(patient_id);
  };

  const handleCloseCMActivitiesModal = () => {
    setIsCMActivitiesModalOpen(false);
    setSelectedPatientId('');
  };

  const navigate = useNavigate();

  const handleRowClick = (
    e: React.MouseEvent | React.KeyboardEvent,
    id: string,
  ) => {
    const url = `/patient/${id}`;

    if (e.metaKey || e.ctrlKey) {
      window.open(url, '_blank', 'noopener,noreferrer');
    } else {
      navigate(url);
    }
  };

  const updatePreferencesMutation = useUpdatePatientTablePreferencesMutation();

  const handleUpdatePreferences = async (
    newPreferences: Partial<
      paths['/preferences/patient-table']['patch']['requestBody']['content']['application/json']
    >,
  ) => {
    await updatePreferencesMutation.mutateAsync(newPreferences);
  };

  const handleChangeOrder = async (field: string, sort: GridSortDirection) => {
    await updatePreferencesMutation.mutateAsync({
      sort_by:
        field as paths['/preferences/patient-table']['patch']['requestBody']['content']['application/json']['sort_by'],
      sort_order: sort ? (sort.toUpperCase() as 'ASC' | 'DESC') : undefined,
    });
  };

  const handleUpdateVisibilityModel = (model: GridColumnVisibilityModel) => {
    type PatientColumns = PatientPreferences['columns'];
    type PatientColumnKey = keyof PatientColumns;

    const currentColumns: PatientPreferences['columns'] =
      preferences?.columns ?? {};
    const updatedColumns: PatientPreferences['columns'] = {};

    const fields = Object.keys(currentColumns) as PatientColumnKey[];

    for (const field of fields) {
      const config = currentColumns[field];

      if (!config) {
        continue;
      }

      const isVisible = model[field];
      const wasHidden = config.column_display === 'hidden';

      let nextDisplay = config.column_display;

      if (isVisible === false) {
        nextDisplay = 'hidden';
      } else if (isVisible === true && wasHidden) {
        nextDisplay = 'display';
      }

      updatedColumns[field] = {
        ...config,
        column_display: nextDisplay,
      };
    }

    handleUpdatePreferences({ columns: updatedColumns });
  };

  const pinnedColumns = usePatientsStore(s => s.pinnedColumns);
  const setPinnedColumns = usePatientsStore(s => s.setPinnedColumns);
  const resetView = usePatientsStore(s => s.resetView);

  const handlePinnedColumnsChange = (model: GridPinnedColumnFields) => {
    setPinnedColumns({
      left: (model.left ?? []).slice(0, MAX_PINNED_COLUMNS),
      right: (model.right ?? []).slice(0, MAX_PINNED_COLUMNS),
    });
  };

  useEffect(() => {
    if (preferences) {
      setFilters({
        ...filters,
        sort_order: lowerCase(preferences.sort_order) as SORT_ORDER,
        limit: preferences.page_size ?? 10,
        sort_by: preferences.sort_by as IPatientsTableFilters['sort_by'],
      });

      setSearchParams(
        prev => {
          return {
            ...prev,
            ...filters,
            sort_by: preferences.sort_by,
            sort_order: preferences.sort_order,
          };
        },
        { replace: true },
      );
    }
  }, [preferences]);

  const displayedColumns = Object.entries(preferences?.columns ?? {})
    .filter(([_key, value]) => value.column_display !== 'hidden')
    .map(([key]) => key);

  const runtimeColumns = buildRecentPatientColumns({
    density: preferences?.density,
    handleOpenCMActivitiesModal,
    displayedColumns,
  });

  const persistedColumns = usePatientsStore(s => s.columns);
  const setPersistedColumns = usePatientsStore(s => s.setColumns);

  const columns = buildGridColumns<
    IRecentPatientTableEntry,
    PersistedPatientsColumn
  >(runtimeColumns, persistedColumns);

  const toPersistedColumn = (
    col: GridColDef<IRecentPatientTableEntry>,
  ): PersistedPatientsColumn => ({
    field: col.field,
    headerName: col.headerName,
    width: col.width,
    minWidth: col.minWidth,
    flex: col.flex,
    sortable: col.sortable,
    filterable: col.filterable,
    disableColumnMenu: col.disableColumnMenu,
    align: col.align,
    headerAlign: col.headerAlign,
  });

  const handleColumnOrderChange = (_params: GridColumnOrderChangeParams) => {
    const next = getReorderedColumns(apiRef, columns);
    setPersistedColumns(next.map(toPersistedColumn));
  };

  const handleColumnWidthChange = (params: GridColumnResizeParams) => {
    const next = columns.map(col =>
      col.field === params.colDef.field
        ? { ...col, width: params.width, flex: undefined }
        : col,
    );

    setPersistedColumns(next.map(toPersistedColumn));
  };

  const handleFullReset = () => {
    resetView();
  };

  const visibilityModel: GridColumnVisibilityModel = {};

  Object.entries(preferences?.columns ?? {}).forEach(([field, config]) => {
    visibilityModel[field] = config?.column_display !== 'hidden';
  });

  const visibleColumnsCount =
    Object.values(visibilityModel).filter(Boolean).length;
  const canHideColumn = visibleColumnsCount > MIN_VISIBLE_COLUMNS;

  const columnsWithHideable = columns.map(col => ({
    ...col,
    hideable: canHideColumn,
  }));

  return {
    apiRef,
    pinnedColumns,
    handlePinnedColumnsChange,
    visibilityModel,
    handleUpdateVisibilityModel,
    filters,
    rows: data.patients,
    columns: columnsWithHideable,
    loading: isFetching || isRefetching,
    isInitialFilters:
      JSON.stringify(filters) === JSON.stringify(initialFilters),
    handleRowClick,
    isCMActivitiesModalOpen,
    handleOpenCMActivitiesModal,
    handleCloseCMActivitiesModal,
    selectedPatientId,
    preferences,
    handleUpdatePreferences,
    handleChangeOrder,
    handleColumnOrderChange,
    handleColumnWidthChange,
    handleFullReset,
    isResetViewModalOpen,
    handleOpenResetViewModal,
    handleCloseResetViewModal,
    initialSortFilters: {
      sort_order: 'asc',
      sort_by: 'name',
    },
  };
};
